<script setup lang="ts">
import PdsSelect from '@/components/pds/PdsSelect.vue';
import type {
  HistoryChannel,
  HistoryPayment,
  HistoryPeriod,
  HistorySort,
} from '../history';
import { usePdsHistoryStore } from '../stores/history.store';

const history = usePdsHistoryStore();

const PERIODS: { value: HistoryPeriod; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'today', label: 'Aujourd’hui' },
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: 'custom', label: 'Personnalisée' },
];
const PAYMENTS: { value: HistoryPayment; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'paid', label: 'Payée' },
  { value: 'pending', label: 'En attente' },
  { value: 'refunded', label: 'Remboursée' },
];
const CHANNELS: { value: HistoryChannel; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'click_and_collect', label: 'Click & collect' },
  { value: 'pos_register', label: 'Caisse' },
];
const SORTS: { value: HistorySort; label: string }[] = [
  { value: 'newer', label: 'Plus récentes' },
  { value: 'older', label: 'Plus anciennes' },
  { value: 'due_asc', label: 'Retrait le plus proche' },
  { value: 'due_desc', label: 'Retrait le plus lointain' },
  { value: 'price_d', label: 'Montant décroissant' },
  { value: 'price', label: 'Montant croissant' },
  { value: 'items_desc', label: 'Plus de produits' },
  { value: 'items_asc', label: 'Moins de produits' },
];

// Un champ vidé ou illisible retire la borne plutôt que d'envoyer NaN.
const toAmount = (value: string) => {
  const amount = Number(value.replace(',', '.'));
  return value.trim() === '' || !Number.isFinite(amount) || amount < 0
    ? null
    : amount;
};

const dateField =
  'bg-carte text-encre h-[58px] rounded-[20px] px-4 text-[16px] shadow-[inset_0_0_0_1px_var(--pds-filet-fort)] outline-none focus:shadow-[inset_0_0_0_2px_var(--pds-encre)]';
</script>

<template>
  <div class="flex flex-wrap items-end gap-3">
    <PdsSelect
      id="history-period"
      label="Période"
      :options="PERIODS"
      :model-value="history.query.period"
      @update:model-value="history.setFilters({ period: $event })"
    />
    <template v-if="history.query.period === 'custom'">
      <label class="flex flex-col gap-1">
        <span class="text-encre-douce text-[12px] font-semibold">Du</span>
        <input
          type="date"
          :class="dateField"
          :value="history.query.from"
          aria-label="Du"
          @change="
            history.setFilters({
              from: ($event.target as HTMLInputElement).value,
            })
          "
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-encre-douce text-[12px] font-semibold">Au</span>
        <input
          type="date"
          :class="dateField"
          :value="history.query.to"
          aria-label="Au"
          @change="
            history.setFilters({
              to: ($event.target as HTMLInputElement).value,
            })
          "
        />
      </label>
    </template>
    <PdsSelect
      id="history-channel"
      label="Canal"
      :options="CHANNELS"
      :model-value="history.query.channel"
      @update:model-value="history.setFilters({ channel: $event })"
    />
    <PdsSelect
      id="history-payment"
      label="Paiement"
      :options="PAYMENTS"
      :model-value="history.query.payment"
      @update:model-value="history.setFilters({ payment: $event })"
    />
    <label class="flex flex-col gap-1">
      <span class="text-encre-douce text-[12px] font-semibold"
        >Montant (€)</span
      >
      <span class="flex items-center gap-2">
        <input
          type="text"
          inputmode="decimal"
          placeholder="Min"
          aria-label="Montant minimum"
          :class="dateField"
          class="w-[100px]"
          :value="history.query.minTotal ?? ''"
          @change="
            history.setFilters({
              minTotal: toAmount(($event.target as HTMLInputElement).value),
            })
          "
        />
        <input
          type="text"
          inputmode="decimal"
          placeholder="Max"
          aria-label="Montant maximum"
          :class="dateField"
          class="w-[100px]"
          :value="history.query.maxTotal ?? ''"
          @change="
            history.setFilters({
              maxTotal: toAmount(($event.target as HTMLInputElement).value),
            })
          "
        />
      </span>
    </label>
    <PdsSelect
      id="history-sort"
      label="Tri"
      :options="SORTS"
      :model-value="history.query.sort"
      @update:model-value="history.setFilters({ sort: $event })"
    />
    <button
      v-if="
        history.activeFilters ||
        history.query.status !== 'all' ||
        history.query.q
      "
      type="button"
      class="text-encre h-[58px] cursor-pointer rounded-[20px] px-4 text-[14px] font-semibold underline"
      @click="history.resetFilters()"
    >
      Réinitialiser
    </button>
  </div>
</template>
